import { useEffect, useCallback, useRef } from 'react'

export const useUpdateMapSource = ({
  map,
  sourceId = 'trees',
  geojson,
  pathname,
  layersReady = false,
}) => {
  const lastGeojsonRef = useRef(null)
  const lastPathnameRef = useRef(null)
  const isInitializedRef = useRef(false)
  const updateTimeoutRef = useRef(null)
  const rotationsMapRef = useRef(new Map())

  // 🎯 Enriquecer GeoJSON con rotación - MEMOIZADO
  const enrichGeojsonWithRotation = useCallback(geojson => {
    if (!geojson?.features?.length) return geojson

    return {
      ...geojson,
      features: geojson.features.map(feature => {
        const featureId = feature.properties.id || feature.properties.code

        // Convertir ht y dap a número (soporta separador decimal con coma, ej: "12,80")
        const parseNum = val => {
          if (val === null || val === undefined || val === '') return null
          const n = parseFloat(String(val).replace(',', '.'))
          return isNaN(n) ? null : n
        }
        const ht = parseNum(feature.properties.ht)
        const dap = parseNum(feature.properties.dap)

        // ✅ Si ya existe rotación, reutilizarla
        if (rotationsMapRef.current.has(featureId)) {
          return {
            ...feature,
            properties: {
              ...feature.properties,
              ht,
              dap,
              randomRotation: rotationsMapRef.current.get(featureId),
            },
          }
        }

        // ✅ Generar nueva rotación
        const newRotation = Math.random() * 270
        rotationsMapRef.current.set(featureId, newRotation)

        return {
          ...feature,
          properties: {
            ...feature.properties,
            ht,
            dap,
            randomRotation: newRotation,
          },
        }
      }),
    }
  }, [])

  // Detectar cambios en datos - MEMOIZADO
  const hasDataChanged = useCallback(newGeojson => {
    const lastGeojson = lastGeojsonRef.current

    if (!lastGeojson && newGeojson?.features?.length) return true
    if (!newGeojson?.features?.length) {
      // Si antes había datos y ahora no, eso es un cambio (ej: filtrado por tenant sin árboles)
      return (lastGeojson?.features?.length || 0) > 0
    }
    if (lastGeojson?.features?.length !== newGeojson.features.length) return true

    // ✅ Comparar por referencia de array (detecta cambios de coordenadas)
    // Si Redux crea un nuevo array, las referencias serán diferentes
    if (lastGeojson.features !== newGeojson.features) return true

    // Fallback: comparar primer feature
    const firstOld = lastGeojson.features[0]
    const firstNew = newGeojson.features[0]

    return (
      firstOld?.id !== firstNew?.id ||
      JSON.stringify(firstOld?.geometry) !== JSON.stringify(firstNew?.geometry)
    )
  }, [])

  // Actualizar fuente - MEMOIZADO ESTABLE
  const updateSource = useCallback(() => {
    if (!map) return

    const source = map.getSource(sourceId)
    if (!source) return

    const rutaIgual = lastPathnameRef.current === pathname
    const datosIguales = !hasDataChanged(geojson)

    if (rutaIgual && datosIguales && isInitializedRef.current) {
      return
    }

    try {
      // Si hay features, enriquecerlas; si no, limpiar la fuente
      const dataToSet = geojson?.features?.length
        ? enrichGeojsonWithRotation(geojson)
        : geojson || { type: 'FeatureCollection', features: [] }
      source.setData(dataToSet)
      lastGeojsonRef.current = dataToSet
      lastPathnameRef.current = pathname
      isInitializedRef.current = true
    } catch (error) {
      console.error(`[useUpdateMapSource] Error al actualizar "${sourceId}":`, error)
    }
  }, [map, sourceId, geojson, pathname, enrichGeojsonWithRotation, hasDataChanged])

  // Setup inicial - ESPERA A QUE CAPAS ESTÉN LISTAS
  useEffect(() => {
    if (!map || !geojson?.features?.length || !layersReady) return

    const waitForSourceAndLayers = async () => {
      let attempts = 0
      const maxAttempts = 50

      while (attempts < maxAttempts) {
        const source = map.getSource(sourceId)
        const hasLayers =
          map.getLayer('clusters') &&
          map.getLayer('unclustered-point') &&
          map.getLayer('trees-3d-default')

        if (source && hasLayers) {
          updateSource()
          return
        }

        await new Promise(resolve => setTimeout(resolve, 100))
        attempts++
      }

      console.error(`[useUpdateMapSource] ❌ Timeout: Fuente o capas no listas`)
    }

    waitForSourceAndLayers()
  }, [map, sourceId, geojson, layersReady, updateSource])

  // Actualizar cuando cambien datos (con debounce)
  useEffect(() => {
    if (!map || !isInitializedRef.current || !layersReady) return

    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current)
    }

    updateTimeoutRef.current = setTimeout(() => {
      if (hasDataChanged(geojson) || lastPathnameRef.current !== pathname) {
        updateSource()
      }
    }, 200)

    return () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current)
      }
    }
  }, [map, geojson, pathname, layersReady, updateSource, hasDataChanged])

  // Cuando se recarga el estilo del mapa
  useEffect(() => {
    if (!map) return

    const handleStyleLoad = () => {
      isInitializedRef.current = false
      lastGeojsonRef.current = null
      lastPathnameRef.current = null
    }

    map.on('style.load', handleStyleLoad)
    return () => {
      map.off('style.load', handleStyleLoad)
    }
  }, [map])

  // 🎯 Métodos públicos
  const forceUpdate = useCallback(() => {
    lastGeojsonRef.current = null
    lastPathnameRef.current = null
    isInitializedRef.current = false
    updateSource()
  }, [updateSource])

  const clearSource = useCallback(() => {
    if (!map) return

    const source = map.getSource(sourceId)
    if (source) {
      source.setData({ type: 'FeatureCollection', features: [] })
      lastGeojsonRef.current = null
      lastPathnameRef.current = null
      isInitializedRef.current = false
    }
  }, [map, sourceId])

  return {
    forceUpdate,
    clearSource,
    isUpdating: !isInitializedRef.current,
  }
}

export default useUpdateMapSource
